import React, { useState } from "react";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  Chip,
  Link,
  Divider,
  IconButton,
  Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SchoolIcon from "@mui/icons-material/School";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import * as XLSX from "xlsx";
import styles from "./learning-path.module.scss";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../store";
import { recordAgentAction } from "../../../store/agentSlice";

type LearningPathAgentProps = {
  parsedContent: any;
};

type Skill = {
  skill_name: string;
  priority_score: number;
  target_level: string;
};

type LearningPath = {
  learning_path_id: string;
  learning_path_name: string;
  description: string;
  source: string;
  type: string;
  duration: string;
  skills_covered: Skill[];
  activities: string[];
};

type LearningPathSummary = {
  online_paths: number;
  blended_paths: number;
  f2f_paths: number;
  certifications: number;
};

type Recommendations = {
  priority_skills: string[];
  recommended_starting_path: string;
  recommended_certifications: string[];
  estimated_completion_time: string;
  learning_intensity: string;
  recommended_weekly_commitment: string;
};

type LearningPathData = {
  learning_path_summary: LearningPathSummary;
  learning_paths_list: LearningPath[];
  recommendations: Recommendations;
  summary_notes: string[];
};

export default function LearningPathAgent({
  parsedContent,
}: LearningPathAgentProps): JSX.Element {
  const dispatch = useAppDispatch();
  const currentAgent = useSelector(
    (state: RootState) => state.agents.currentAgent,
  );
  const numericAgentId = currentAgent?.id || 14;
  const [expandedPath, setExpandedPath] = useState<string | false>("path-0");

  // Parse the learning path data
  let learningPathData: LearningPathData | null = null;
  const finalAnswer = parsedContent?.finalAnswer;

  try {
    if (Array.isArray(finalAnswer) && finalAnswer.length > 0) {
      learningPathData = JSON.parse(finalAnswer[0]);
    } else if (typeof finalAnswer === "string" && finalAnswer.trim() !== "") {
      learningPathData = JSON.parse(finalAnswer);
    } else if (typeof parsedContent === "object" && parsedContent.learning_paths_list) {
      learningPathData = parsedContent;
    }
  } catch (error) {
    console.error("Error parsing learning path data:", error);
  }

  // If no learning path data but question exists, show question message
  const question = parsedContent?.question;
  if (!learningPathData && question) {
    return (
      <Box className={styles.questionOnlyContainer}>
        <Box className={styles.questionBox}>
          <InfoOutlinedIcon sx={{ color: "#00a888", fontSize: 20 }} />
          <Typography className={styles.questionText}>{question}</Typography>
        </Box>
      </Box>
    );
  }

  const handlePathChange =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedPath(isExpanded ? panel : false);
    };

  const getPriorityColor = (priority: number): string => {
    if (priority >= 80) return "#f44336";
    if (priority >= 60) return "#ff9800";
    return "#4caf50";
  };

  const handleExportExcel = () => {
    if (!learningPathData) return;

    try {
      const excelData: any[][] = [];

      // Title Row
      excelData.push(["YOUR PERSONALIZED LEARNING PATHS"]);
      excelData.push([]);

      // Summary Section
      if (learningPathData.learning_path_summary) {
        const summary = learningPathData.learning_path_summary;
        excelData.push(["SUMMARY"]);
        excelData.push(["Online Paths", summary.online_paths]);
        excelData.push(["Blended Paths", summary.blended_paths]);
        excelData.push(["Face-to-Face Paths", summary.f2f_paths]);
        excelData.push(["Certifications", summary.certifications]);
        excelData.push([]);
      }

      // Recommendations Section
      if (learningPathData.recommendations) {
        const rec = learningPathData.recommendations;
        excelData.push(["RECOMMENDATIONS"]);
        excelData.push(["Estimated Completion Time", rec.estimated_completion_time || ""]);
        excelData.push(["Learning Intensity", rec.learning_intensity || ""]);
        excelData.push(["Recommended Weekly Commitment", rec.recommended_weekly_commitment || ""]);
        excelData.push(["Recommended Starting Path", rec.recommended_starting_path || ""]);

        if (rec.priority_skills && rec.priority_skills.length > 0) {
          excelData.push(["Priority Skills", rec.priority_skills.join(", ")]);
        }

        if (rec.recommended_certifications && rec.recommended_certifications.length > 0) {
          excelData.push(["Recommended Certifications"]);
          rec.recommended_certifications.forEach((cert) => {
            excelData.push(["", `• ${cert}`]);
          });
        }
        excelData.push([]);
      }

      // Learning Paths Section
      excelData.push(["LEARNING PATHS"]);
      excelData.push([]);

      learningPathData.learning_paths_list.forEach((path, index) => {
        // Path Header
        excelData.push([`PATH ${index + 1}: ${path.learning_path_name.toUpperCase()}`]);
        excelData.push(["Type", path.type]);
        excelData.push(["Duration", path.duration]);
        excelData.push(["Description", path.description]);
        excelData.push([]);

        // Skills Covered
        if (path.skills_covered && path.skills_covered.length > 0) {
          excelData.push(["Skills Covered", "Priority Score", "Target Level"]);
          path.skills_covered.forEach((skill) => {
            excelData.push([
              skill.skill_name,
              skill.priority_score,
              skill.target_level,
            ]);
          });
          excelData.push([]);
        }

        // Activities
        if (path.activities && path.activities.length > 0) {
          excelData.push(["Activities"]);
          path.activities.forEach((activity, actIdx) => {
            excelData.push([`${actIdx + 1}.`, activity]);
          });
          excelData.push([]);
        }

        // Source
        if (path.source) {
          excelData.push(["Source", path.source]);
        }

        excelData.push([]);
        excelData.push([]);
      });

      // Summary Notes Section
      if (learningPathData.summary_notes && learningPathData.summary_notes.length > 0) {
        excelData.push(["KEY NOTES"]);
        excelData.push([]);
        learningPathData.summary_notes.forEach((note, index) => {
          excelData.push([`${index + 1}.`, note]);
        });
        excelData.push([]);
      }

      // Create worksheet
      const ws = XLSX.utils.aoa_to_sheet(excelData);

      // Set column widths
      const columnWidths = [
        { wch: 35 }, // Column A (Labels / Activity numbers)
        { wch: 120 }, // Column B (Main content)
        { wch: 20 }, // Column C (Target Level)
      ];
      ws["!cols"] = columnWidths;

      // Enable text wrapping for all cells
      const range = XLSX.utils.decode_range(ws["!ref"] || "A1");
      for (let R = range.s.r; R <= range.e.r; ++R) {
        for (let C = range.s.c; C <= range.e.c; ++C) {
          const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
          if (!ws[cellAddress]) continue;
          if (!ws[cellAddress].s) ws[cellAddress].s = {};
          ws[cellAddress].s.alignment = { wrapText: true, vertical: "top" };
        }
      }

      // Create workbook
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Learning Paths");

      // Generate filename
      const timestamp = new Date().toISOString().split("T")[0];
      const fileName = `learning-paths-${timestamp}.xlsx`;

      // Record action
      dispatch(
        recordAgentAction({
          agentId: numericAgentId,
          actionType: "download",
          content: JSON.stringify(learningPathData),
        }),
      );

      // Download file
      XLSX.writeFile(wb, fileName);
    } catch (error) {
      console.error("Error exporting Excel:", error);
    }
  };

  if (!learningPathData) {
    return (
      <Box className={styles.errorContainer}>
        <Typography color="error">
          Unable to load learning path data. Please try again.
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={styles.learningPathContainer}>
      {/* Header Section */}
      <Box className={styles.headerSection}>
        <Box className={styles.headerIcon}>
          <SchoolIcon sx={{ fontSize: 32, color: "#00a888" }} />
        </Box>
        <Typography className={styles.headerTitle}>
          Your Personalized Learning Paths
        </Typography>
        {learningPathData.learning_path_summary && (
          <Typography className={styles.headerSubtitle}>
            {learningPathData.learning_path_summary.online_paths} Online • {learningPathData.learning_path_summary.blended_paths} Blended • {learningPathData.learning_path_summary.f2f_paths} F2F • {learningPathData.learning_path_summary.certifications} Certifications
          </Typography>
        )}
      </Box>

      {/* Recommendations Section */}
      {learningPathData.recommendations && (
        <Card className={styles.summaryCard}>
          <CardContent>
            <Box className={styles.summaryHeader}>
              <InfoOutlinedIcon sx={{ color: "#00a888", fontSize: 20 }} />
              <Typography className={styles.sectionTitle}>Recommendations</Typography>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {learningPathData.recommendations.estimated_completion_time && (
                <Box className={styles.timelineBox}>
                  <AccessTimeIcon sx={{ fontSize: 18, color: "#6b7280" }} />
                  <Typography className={styles.timelineText}>
                    {learningPathData.recommendations.estimated_completion_time}
                  </Typography>
                </Box>
              )}
              {learningPathData.recommendations.recommended_weekly_commitment && (
                <Typography sx={{ fontSize: "14px", color: "#4b5563" }}>
                  <strong>Weekly Commitment:</strong> {learningPathData.recommendations.recommended_weekly_commitment}
                </Typography>
              )}
              {learningPathData.recommendations.priority_skills && learningPathData.recommendations.priority_skills.length > 0 && (
                <Box>
                  <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#6b7280", mb: 1 }}>
                    Priority Skills:
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {learningPathData.recommendations.priority_skills.map((skill, idx) => (
                      <Chip
                        key={idx}
                        label={skill}
                        size="small"
                        sx={{
                          backgroundColor: "#e8f5f3",
                          color: "#00a888",
                          fontWeight: 500,
                          fontSize: "11px",
                          border: "1px solid #b3e0d9",
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Learning Paths Section */}
      <Box className={styles.phasesSection}>
        <Typography className={styles.mainSectionTitle}>
          Learning Paths
        </Typography>
        {learningPathData.learning_paths_list.map((path, pathIndex) => (
          <Accordion
            key={pathIndex}
            expanded={expandedPath === `path-${pathIndex}`}
            onChange={handlePathChange(`path-${pathIndex}`)}
            className={styles.phaseAccordion}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              className={styles.phaseHeader}
            >
              <Box className={styles.phaseHeaderContent}>
                <Typography className={styles.phaseName}>
                  {path.learning_path_name}
                </Typography>
                <Chip
                  label={path.type}
                  size="small"
                  className={styles.courseCountChip}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails className={styles.phaseContent}>
              <Box className={styles.objectiveBox}>
                <TrendingUpIcon sx={{ fontSize: 18, color: "#00a888" }} />
                <Typography className={styles.objectiveText}>
                  {path.description}
                </Typography>
              </Box>

              {/* Duration and Source */}
              <Box sx={{ mb: 2, display: "flex", gap: 2, flexWrap: "wrap" }}>
                <Chip
                  label={path.duration}
                  size="small"
                  icon={<AccessTimeIcon sx={{ fontSize: 14 }} />}
                  sx={{
                    backgroundColor: "#fff7ed",
                    color: "#ea580c",
                    fontWeight: 600,
                    fontSize: "11px",
                    border: "1px solid #fed7aa",
                  }}
                />
              </Box>

              {/* Skills Covered */}
              {path.skills_covered && path.skills_covered.length > 0 && (
                <Box className={styles.skillGapsBox}>
                  <Typography className={styles.skillGapsLabel}>
                    Skills covered:
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {path.skills_covered.map((skill, idx) => (
                      <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Chip
                          label={skill.skill_name}
                          size="small"
                          sx={{
                            backgroundColor: "#e8f5f3",
                            color: "#00a888",
                            fontWeight: 500,
                            fontSize: "11px",
                            border: "1px solid #b3e0d9",
                            flex: 1,
                          }}
                        />
                        <Chip
                          label={`Priority: ${skill.priority_score}`}
                          size="small"
                          sx={{
                            backgroundColor: getPriorityColor(skill.priority_score),
                            color: "#fff",
                            fontWeight: 600,
                            fontSize: "11px",
                            minWidth: "100px",
                          }}
                        />
                        <Chip
                          label={skill.target_level}
                          size="small"
                          sx={{
                            backgroundColor: "#f3f4f6",
                            color: "#4b5563",
                            fontWeight: 600,
                            fontSize: "11px",
                            border: "1px solid #d1d5db",
                          }}
                        />
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {/* Activities */}
              {path.activities && path.activities.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#1a1a1a", mb: 1.5 }}>
                    Activities:
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    {path.activities.map((activity, idx) => (
                      <Box key={idx} sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                        <CheckCircleOutlineIcon sx={{ fontSize: 18, color: "#00a888", mt: 0.2, flexShrink: 0 }} />
                        <Typography sx={{ fontSize: "13px", lineHeight: 1.6, color: "#374151" }}>
                          {activity}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {/* Source */}
              {path.source && (
                <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid #e5e7eb" }}>
                  <Typography sx={{ fontSize: "12px", color: "#6b7280" }}>
                    <strong>Source:</strong> {path.source}
                  </Typography>
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>

      {/* Summary Notes Section */}
      {learningPathData.summary_notes && learningPathData.summary_notes.length > 0 && (
        <Card className={styles.milestonesCard}>
          <CardContent>
            <Box className={styles.milestonesHeader}>
              <CheckCircleOutlineIcon
                sx={{ color: "#00a888", fontSize: 22 }}
              />
              <Typography className={styles.sectionTitle}>
                Key Notes
              </Typography>
            </Box>
            <Box className={styles.milestonesList}>
              {learningPathData.summary_notes.map((note, index) => (
                <Box key={index} className={styles.milestoneItem}>
                  <Box className={styles.milestoneNumber}>{index + 1}</Box>
                  <Typography className={styles.milestoneText}>
                    {note}
                  </Typography>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Recommended Certifications */}
      {learningPathData.recommendations?.recommended_certifications && learningPathData.recommendations.recommended_certifications.length > 0 && (
        <Card className={styles.notesCard}>
          <CardContent>
            <Typography className={styles.notesTitle}>
              Recommended Certifications
            </Typography>
            <Divider sx={{ my: 1.5 }} />
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {learningPathData.recommendations.recommended_certifications.map((cert, idx) => (
                <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CheckCircleOutlineIcon sx={{ fontSize: 18, color: "#92400e" }} />
                  <Typography className={styles.notesText}>
                    {cert}
                  </Typography>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Export Section */}
      <Box className={styles.downloadSection}>
        <Box className={styles.downloadContent} sx={{ cursor: "pointer" }}>
          <FileDownloadIcon className={styles.downloadSectionIcon} />
          <Box className={styles.downloadText}>
            <Typography className={styles.downloadTitle}>
              Export to Excel
            </Typography>
            <Typography className={styles.downloadDescription}>
              Get a comprehensive Excel report of your personalized learning paths
            </Typography>
          </Box>
        </Box>
        <Tooltip title="Export to Excel" arrow>
          <IconButton
            className={styles.downloadButton}
            onClick={handleExportExcel}
            aria-label="Export Excel Report"
          >
            <FileDownloadIcon className={styles.downloadButtonIcon} />
            <span>Export Excel</span>
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}
